<template>
  <div>
    <LoadingContainer v-if="loading"/>

    <div v-else class="container">
      <div class="row">
        <div class="col-xs-6">
          <HelpText>
            <h3>
              Create Opportunity
            </h3>

            <p>
              Enter the details of the opportunity.
            </p>
          </HelpText>
        </div>

        <div class="col-xs-18">
          <div class="panel panel-default">
            <div class="panel-body">
              <form @submit.prevent="submit" class="form-horizontal">
                <TextFormGroup
                    v-model="data.title"
                    name="title"
                    label="Title"
                    help-text="A title for the opportunity."
                    :error="(errors.title || [])[0]"
                />

                <NumberFormGroup
                    v-model="data.value"
                    name="value"
                    label="Value"
                    help-text="The value of the opportunity."
                    :error="(errors.value || [])[0]"
                />

                <SelectFormGroup
                    v-model="data.category"
                    name="category"
                    label="Category"
                    help-text="The category that the opportunity belongs to."
                    :error="(errors.category || [])[0]"
                    :options="[{value: 'advertising', label: 'Advertising'}, {value: 'subscription', label: 'Subscription'}]"
                />

                <DateFormGroup
                    v-model="data.conversion_expected_at"
                    name="conversion_expected_at"
                    label="Conversion Expected At"
                    help-text="The expected date that the opportunity will be converted."
                    :error="(errors.conversion_expected_at || [])[0]"
                    format="DD.MM.YYYY"
                />

                <TypeaheadFormGroup
                    v-model="data.user_id"
                    name="user_id"
                    label="User"
                    endpoint="/api/v2/users"
                    :token="shared.token"
                    path="data.data"
                    placeholder="Search..."
                    :result-label-property="row => row.attributes.full_name"
                    help-text="The user that is responsible for the opportunity."
                />

                <SelectFormGroup
                    v-model="data.portal_id"
                    name="portal_id"
                    label="Portal"
                    help-text="The portal that the opportunity relates to."
                    :error="(errors.portal_id || [])[0]"
                    :options="portalOptions"
                />

                <SelectWithButtonFormGroup
                    v-model="data.contact_person_id"
                    name="contact_person_id"
                    label="Contact Person"
                    help-text="The contact person in the company."
                    :error="(errors.contact_person_id || [])[0]"
                    :options="personOptions"
                    :nullable="true"
                    @click="openPersonModal"
                />

                <SelectFormGroup
                    v-model="data.project_id"
                    name="project_id"
                    label="Project"
                    help-text="The project that the opportunity relates to."
                    :error="(errors.project_id || [])[0]"
                    :options="projectOptions"
                    :nullable="true"
                />

                <SelectFormGroup
                    v-model="data.meeting_id"
                    name="meeting_id"
                    label="Meeting"
                    help-text="The meeting that the opportunity was created from."
                    :error="(errors.meeting_id || [])[0]"
                    :options="meetingOptions"
                    :nullable="true"
                />

                <RadioFormGroup
                    v-if="!data.meeting_id"
                    v-model="data.is_new_business"
                    name="is_new_business"
                    label="New Business?"
                    :error="(errors.is_new_business || [])[0]"
                    :options="[{value: true, label: 'Yes'}, {value: false, label: 'No'}]"
                />

                <hr>

                <SubmitFormGroup button-class="btn-black"/>
              </form>
            </div>
          </div>
        </div>
      </div>

      <CreatePersonModal :company="company.data.id" @created="personCreated"/>
    </div>
  </div>
</template>

<script>
import http from '@/http'
import notify from '@/notify'
import store from '@/store'
import CreatePersonModal from '@/pages/Companies/Companies/Edit/Modals/CreatePerson'
import DateFormGroup from '@/vendor/forms/FormGroups/Horizontal/Date'
import Errors from '@/mixins/Errors'
import HelpText from '@/components/HelpText'
import LoadingContainer from '@/components/Layout/LoadingContainer'
import NumberFormGroup from '@/vendor/forms/FormGroups/Horizontal/Number'
import RadioFormGroup from '@/vendor/forms/FormGroups/Horizontal/Radio'
import SelectFormGroup from '@/vendor/forms/FormGroups/Horizontal/Select'
import SelectWithButtonFormGroup from '@/components/FormGroups/Horizontal/SelectWithButton'
import SubmitFormGroup from '@/vendor/forms/FormGroups/Horizontal/Submit'
import TextFormGroup from '@/vendor/forms/FormGroups/Horizontal/Text'
import TypeaheadFormGroup from "../../../../../vendor/forms/FormGroups/Horizontal/Typeahead.vue";

export default {
  components: {
    TypeaheadFormGroup,
    CreatePersonModal,
    DateFormGroup,
    HelpText,
    LoadingContainer,
    NumberFormGroup,
    RadioFormGroup,
    SelectFormGroup,
    SelectWithButtonFormGroup,
    SubmitFormGroup,
    TextFormGroup
  },

  mixins: [Errors],

  model: {
    prop: 'company',
    event: 'company-updated'
  },

  props: ['company'],

  data() {
    return {
      shared: store,
      data: {
        meeting_id: this.$route.query.meeting_id
      },

      portals: null,
      people: null,
      projects: null,

      loading: true
    }
  },

  mounted() {
    this.load()
  },

  computed: {
    portalOptions() {
      return this.portals.map(portal => ({value: portal.id, label: portal.attributes.name}))
    },

    personOptions() {
      return this.people
          .filter(person => person.is_active)
          .map(person => ({value: person.id, label: person.name}))
    },

    meetingOptions() {
      return this.meetings.map(meeting => (
          {
            value: meeting.id,
            label: (meeting.attributes.title || 'Untitled meeting') + ' (' + this.$options.filters.datetime(meeting.attributes.starts_at) + ')'
          }
      ))
    },

    projectOptions() {
      return this.projects.map(project => ({value: project.id, label: project.attributes.portal_name + ' - ' + project.attributes.project_category_name + ' - ' + project.attributes.name}))
    },
  },

  methods: {
    load() {
      this.loading = true

      Promise.all([
        http.get('/api/v2/portals'),
        http.get('/api/v1/companies/' + this.company.data.id + '/people'),
        http.get('/api/v2/meetings', {'page_size': 100, 'filter[company_id]': this.company.data.id}),
        http.get('/api/v2/projects', {'page_size': 100, 'filter[status]': 'active'}),
      ])
          .then(responses => {
            this.portals = responses[0].data.data

            this.people = responses[1].data.data

            this.meetings = responses[2].data.data

            this.projects = responses[3].data.data

            this.loading = false
          })
    },

    submit() {
      http.post('/api/v2/opportunities', {
        ...this.data,
        company_id: this.company.data.id,
        user_id: this.shared.user.id,
      })
          .then(() => {
            notify.success('The opportunity was saved')

            this.$router.push('.')
          })
          .catch(this.catchValidationErrors)
    },

    openPersonModal() {
      $('#create-person-modal').modal('show')
    },

    personCreated(person) {
      this.people.push(person)

      this.people.sort((a, b) => a.name.toLowerCase() >= b.name.toLowerCase() ? 1 : -1)

      this.$nextTick(() => {
        this.data.contact_person_id = person.id
      })
    }
  },

  watch: {
    'data.portal_id'(value) {
      http.get('/api/v2/projects', {'page_size': 100, 'filter[status]': 'active', 'filter[portal_id]': value})
          .then(response => {
            this.projects = response.data.data

            if (!this.projects.map(project => project.id).includes(this.data.project_id)) {
              this.data.project_id = null
            }
          })
    }
  }
}
</script>
